import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SECTEURS_DATA } from "@/lib/motion";
import { Reveal, Overline } from "@/components/Reveal";
import { useSEO } from "@/lib/useSEO";
import { BookingButton } from "@/components/BookingButton";

const SECTORS = SECTEURS_DATA;

const CLIENTS = [
  { t: "États, ministères et collectivités", d: "Éclairer les choix publics structurants et renforcer la capacité à concevoir et piloter des projets d'envergure." },
  { t: "Agences publiques et autorités contractantes", d: "Sécuriser la préparation, la passation et le suivi des projets confiés au secteur privé." },
  { t: "Institutions financières de développement & bailleurs de fonds", d: "Apporter un regard indépendant sur la qualité, la robustesse et l'impact des projets financés." },
  { t: "Investisseurs & développeurs de projets", d: "Fiabiliser les décisions d'investissement par une lecture rigoureuse des risques et des opportunités." },
  { t: "Entreprises & organisations", d: "Accompagner les transformations stratégiques, organisationnelles et opérationnelles créatrices de valeur." },
];

export default function Secteurs() {
  const { hash } = useLocation();
  useSEO({
    title: "Secteurs d'intervention",
    description:
      "HERUS Axiom intervient dans l'énergie, les infrastructures, l'industrie et le secteur public, auprès des États, agences, institutions financières de développement, investisseurs et entreprises.",
    keywords: "énergie, infrastructures, industrie, secteur public, États, bailleurs de fonds, institutions financières de développement, investisseurs, entreprises",
    path: "/secteurs",
  });

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div data-testid="secteurs-page" className="bg-[#0A0A0A]">
      {/* Hero — Section 9 */}
      <section className="pt-40 lg:pt-52 pb-20 mx-auto max-w-[1600px] px-6 lg:px-12">
        <Reveal><Overline>Secteurs d'intervention</Overline></Reveal>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          className="mt-8 font-serif-display font-light leading-[0.95] tracking-tight text-[#F9F6F0] text-4xl sm:text-5xl lg:text-7xl max-w-5xl"
        >
          Là où les projets structurent
          <br />
          durablement les <span className="italic text-[#C5A059]">territoires.</span>
        </motion.h1>
        <Reveal i={2}>
          <p className="mt-10 max-w-2xl text-[#F9F6F0]/70 text-base lg:text-lg font-light leading-relaxed">
            HERUS accompagne les organisations intervenant dans des secteurs où les enjeux de
            gouvernance, de transformation et de développement exigent une approche stratégique, une
            compréhension des réalités opérationnelles et une vision de long terme.
          </p>
        </Reveal>
      </section>

      {/* Sectors */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pb-24">
        <div className="border-t border-white/10">
          {SECTORS.map((s, i) => (
            <Reveal key={s.n} i={i % 2} id={s.id} className="scroll-mt-28 group grid lg:grid-cols-12 gap-8 items-center border-b border-white/10 py-10 lg:py-14">
              <div className="lg:col-span-1">
                <span className="font-serif-display text-4xl text-[#C5A059]">{s.n}</span>
              </div>
              <div className="lg:col-span-4 relative overflow-hidden h-52 lg:h-64">
                <img src={s.img} alt={s.t} className="absolute inset-0 h-full w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
              </div>
              <div className="lg:col-span-7">
                <h2 className="font-serif-display text-3xl lg:text-4xl font-light text-[#F9F6F0] group-hover:text-[#C5A059] transition-colors duration-500">{s.t}</h2>
                <p className="mt-4 max-w-2xl text-[#F9F6F0]/60 font-light leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Pour qui nous intervenons */}
      <section className="bg-[#F9F6F0] py-24 lg:py-32">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <Reveal>
            <Overline>Pour qui nous intervenons</Overline>
            <h2 className="mt-6 font-serif-display text-3xl lg:text-4xl font-light italic text-[#0A0A0A]">
              Des expertises adaptées aux enjeux de chaque acteur.
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#0A0A0A]/10">
            {CLIENTS.map((c, i) => (
              <Reveal key={i} i={i % 3} className="border-b border-r border-[#0A0A0A]/10 p-10 lg:p-12">
                <span className="font-serif-display text-5xl text-[#C5A059] leading-none block">0{i + 1}</span>
                <h3 className="mt-5 font-serif-display text-2xl text-[#0A0A0A] leading-snug">{c.t}</h3>
                <p className="mt-3 text-[#0A0A0A]/60 font-light leading-relaxed text-sm">{c.d}</p>
              </Reveal>
            ))}
            <Reveal i={2} className="border-b border-r border-[#0A0A0A]/10 p-10 lg:p-12 flex flex-col justify-center bg-[#0A0A0A]">
              <h3 className="font-serif-display text-2xl text-[#F9F6F0] leading-snug">Un enjeu spécifique ?</h3>
              <Link to="/contact" data-testid="clients-cta" className="group inline-flex items-center gap-3 mt-6 text-sm uppercase tracking-[0.2em] text-[#F9F6F0] border-b border-[#C5A059] pb-2 self-start">
                Parlez-nous de votre projet
                <ArrowUpRight size={18} className="text-[#C5A059] transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <BookingButton testId="secteurs-booking-btn" label="Réserver 30 min" className="mt-6 self-start inline-flex items-center gap-2 border border-[#C5A059] text-[#C5A059] text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#C5A059] hover:text-[#0A0A0A] transition-colors duration-500" />
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
